interface CacheEntry {
  token: string;
  embedUrl: string;
  timestamp: number;
  lastUsed: number;
  isValid: boolean;
}

class TableauAuthCache {
  private cache: Map<string, CacheEntry> = new Map();
  private sessionValid: boolean = false;
  private readonly TOKEN_EXPIRY = 1000 * 60 * 30; // 30 minutes (more realistic for Tableau)
  private readonly TOKEN_REFRESH_THRESHOLD = 1000 * 30; // Refresh if more than 30 seconds old (aggressive refresh)
  private readonly TOKEN_MAX_AGE = 1000 * 60 * 2; // Force refresh if token is older than 2 minutes
  private readonly MAX_RETRY_ATTEMPTS = 3;
  private retryCount: Map<string, number> = new Map();

  set(viewPath: string, token: string, embedUrl: string): void {
    console.log('TableauAuthCache: Caching token for viewPath:', viewPath);
    this.cache.set(viewPath, {
      token,
      embedUrl,
      timestamp: Date.now(),
      lastUsed: Date.now(),
      isValid: true,
    });
    // Reset retry count on successful cache
    this.retryCount.delete(viewPath);
  }

  get(viewPath: string): { token: string; embedUrl: string } | null {
    const entry = this.cache.get(viewPath);
    if (!entry) {
      console.log('TableauAuthCache: No cached entry for viewPath:', viewPath);
      return null;
    }

    const now = Date.now();
    const age = now - entry.timestamp;

    // Check if token is expired
    if (age > this.TOKEN_EXPIRY) {
      console.log('TableauAuthCache: Token expired for viewPath:', viewPath, 'age:', age);
      this.cache.delete(viewPath);
      this.retryCount.delete(viewPath);
      return null;
    }

    // Check if token is marked as invalid (due to authentication failure)
    if (!entry.isValid) {
      console.log('TableauAuthCache: Token marked as invalid for viewPath:', viewPath);
      this.cache.delete(viewPath);
      this.retryCount.delete(viewPath);
      return null;
    }

    // Check if token is too old for reliable use (more than 2 minutes)
    if (age > this.TOKEN_MAX_AGE) {
      console.log('TableauAuthCache: Token too old for reliable use, forcing refresh for viewPath:', viewPath, 'age:', age);
      this.cache.delete(viewPath);
      this.retryCount.delete(viewPath);
      return null;
    }

    // Check if we should refresh the token (more than 30 seconds old)
    if (age > this.TOKEN_REFRESH_THRESHOLD) {
      console.log('TableauAuthCache: Token needs refresh for viewPath:', viewPath, 'age:', age);
      // Don't delete, but mark for refresh
      entry.isValid = false;
    }

    // Update last used timestamp
    entry.lastUsed = now;

    console.log('TableauAuthCache: Using cached token for viewPath:', viewPath, 'age:', age);
    return {
      token: entry.token,
      embedUrl: entry.embedUrl,
    };
  }

  markTokenInvalid(viewPath: string): void {
    console.log('TableauAuthCache: Marking token as invalid for viewPath:', viewPath);
    const entry = this.cache.get(viewPath);
    if (entry) {
      entry.isValid = false;
    }
    
    // Increment retry count
    const currentRetries = this.retryCount.get(viewPath) || 0;
    this.retryCount.set(viewPath, currentRetries + 1);
    
    // If too many retries, clear the cache for this viewPath
    if (currentRetries >= this.MAX_RETRY_ATTEMPTS) {
      console.log('TableauAuthCache: Too many retries, clearing cache for viewPath:', viewPath);
      this.cache.delete(viewPath);
      this.retryCount.delete(viewPath);
    }
  }

  shouldRetry(viewPath: string): boolean {
    const retries = this.retryCount.get(viewPath) || 0;
    return retries < this.MAX_RETRY_ATTEMPTS;
  }

  setSession(valid: boolean): void {
    console.log('TableauAuthCache: Setting session valid:', valid);
    this.sessionValid = valid;
    
    // If session is invalid, clear all cached tokens
    if (!valid) {
      console.log('TableauAuthCache: Session invalid, clearing all cached tokens');
      this.cache.clear();
      this.retryCount.clear();
    }
  }

  isSessionValid(): boolean {
    return this.sessionValid;
  }

  clear(): void {
    console.log('TableauAuthCache: Clearing all cache');
    this.cache.clear();
    this.retryCount.clear();
    this.sessionValid = false;
  }

  clearCache(): void {
    this.clear();
  }

  // Get cache statistics for debugging
  getCacheStats(): { totalEntries: number; validEntries: number; expiredEntries: number } {
    const now = Date.now();
    let validEntries = 0;
    let expiredEntries = 0;

    this.cache.forEach((entry) => {
      const age = now - entry.timestamp;
      if (age > this.TOKEN_EXPIRY || !entry.isValid) {
        expiredEntries++;
      } else {
        validEntries++;
      }
    });

    return {
      totalEntries: this.cache.size,
      validEntries,
      expiredEntries,
    };
  }
}

export const tableauAuthCache = new TableauAuthCache();
